import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Tooltip, OverlayTrigger } from "../../../evo/bootstrap";
import { validateElevenLabsKey, createAgent, getAgent } from "../../Services/chatbot";
import "../../assets/agentsetup.css";

const getVoiceDisplayName = (voice) => {
  const full = voice.name || "";
  return full.includes(" - ") ? full.split(" - ")[0].trim() : full;
};

const getVoiceDescription = (voice) => {
  const full = voice.name || "";
  return full.includes(" - ") ? full.split(" - ").slice(1).join(" - ").trim() : "";
};

const getVoiceLabel = (voice, key) => voice.labels?.[key] || "";

const formatLabel = (val) => {
  if (!val) return "";
  return val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const DEFAULT_SYSTEM_PROMPT = `You are a friendly and professional hotel concierge AI assistant. Your role is to help guests with room bookings and reservations, hotel amenities and services information, local attractions and recommendations, check-in/check-out assistance, dining reservations, room service, and general guest inquiries. Always be warm, helpful, and speak in a clear and professional tone. For all hotel-specific information, tool calls, searches, bookings, reservations, availability checks, and guest requests, you must use the provided {{property_id}} to identify the correct hotel property and retrieve accurate data. Never assume a property or use data from another property. Always pass {{property_id}} to any relevant tool, API, database query, or service call. If a guest's request requires hotel data, first retrieve information using {{property_id}} before responding. If multiple properties exist, ensure all responses are scoped only to the property associated with {{property_id}}. If you cannot answer a question or complete a request, politely inform the guest and offer to connect them with a human agent for further assistance.`;

const DEFAULT_CONFIG = {
  api_key: "",
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  first_message: "Hello! Welcome",
  voice_id: "",
};

const InfoTooltip = ({ id, text }: any) => (
  <OverlayTrigger
    placement="top"
    overlay={<Tooltip id={id}>{text}</Tooltip>}
  >
    <i
      className="bi bi-info-circle ms-2 text-primary"
      role="button"
      style={{ fontSize: "13px" }}
    ></i>
  </OverlayTrigger>
);

const AgentSetup = ({ isDirty, setIsDirty, setIsNeedPublish, setPublishTrigger }: any) => {
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [config, setConfig] = useState<any>({ ...DEFAULT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keyStatus, setKeyStatus] = useState("idle");
  const [voices, setVoices] = useState<any>([]);
  const [showApiKey, setShowApiKey] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<any>(null);
  const [voiceSearch, setVoiceSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [existingAgent, setExistingAgent] = useState<any>(null);
  const audioElRef = useRef<any>(null);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await getAgent();
        const data = res?.data || {};
        if (data.agent_id) {
          setExistingAgent(data);
          setConfig((prev) => ({
            ...prev,
            system_prompt: data.system_prompt || prev.system_prompt,
            first_message: data.first_message || prev.first_message,
            voice_id: data.voice_id || prev.voice_id,
          }));
          if (Array.isArray(data.voices) && data.voices.length > 0) {
            setVoices(data.voices);
          }
          setSelectedProvider("elevenlabs");
          setKeyStatus("existing");
        }
      } catch (err) {
        console.error("get-agent error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, []);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSelectProvider = () => {
    if (existingAgent) return;
    setSelectedProvider("elevenlabs");
    setConfig({ ...DEFAULT_CONFIG });
    setKeyStatus("idle");
    setVoices([]);
    setVoiceSearch("");
    setGenderFilter("all");
    setIsDirty(true);
  };

  const handleValidateKey = async () => {
    if (!config.api_key.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    setKeyStatus("validating");
    try {
      const res = await validateElevenLabsKey({ elevenlabs_api_key: config.api_key.trim() });
      const data = res?.data || {};

      if (data.valid === false) {
        setKeyStatus(existingAgent ? "existing" : "invalid");
        setVoices([]);
        toast.error(data.error || "Invalid API key. Please check your dashboard and try again.");
        return;
      }

      const voiceList = data.voices || [];
      setVoices(voiceList);
      setKeyStatus("valid");
      toast.success("API key validated — voices loaded!");
    } catch (err) {
      setKeyStatus(existingAgent ? "existing" : "invalid");
      setVoices([]);
      const errData = err?.response?.data;
      const msg = (errData?.valid === false && errData?.error)
        ? errData.error
        : (err?.message || "Invalid API key. Please check your dashboard and try again.");
      toast.error(msg);
      console.error("Validate key error:", err);
    }
  };

  const handlePreviewVoice = (voice) => {
    const el = audioElRef.current;
    if (!el) return;

    if (playingVoiceId === voice.voice_id) {
      el.pause();
      el.currentTime = 0;
      setPlayingVoiceId(null);
      return;
    }

    if (!voice.preview_url) {
      toast.info("No preview available for this voice");
      return;
    }

    el.pause();
    el.src = voice.preview_url;
    el.load();
    setPlayingVoiceId(voice.voice_id);

    const playWhenReady = () => {
      el.play().catch((err) => {
        console.error("Audio play error:", err);
        setPlayingVoiceId(null);
        toast.error("Unable to play voice preview");
      });
    };

    if (el.readyState >= 2) {
      playWhenReady();
    } else {
      el.oncanplay = () => {
        el.oncanplay = null;
        playWhenReady();
      };
    }

    el.onended = () => setPlayingVoiceId(null);
    el.onerror = () => {
      setPlayingVoiceId(null);
      toast.error("Unable to play voice preview");
    };
  };

  const handleSave = async () => {
    if (!existingAgent) {
      if (!config.api_key.trim()) {
        toast.error("Please connect your account first");
        return;
      }
      if (keyStatus !== "valid") {
        toast.error("Please validate your API key before saving");
        return;
      }
    }
    if (!config.voice_id) {
      toast.error("Please select a voice before saving");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        system_prompt: config.system_prompt,
        first_message: config.first_message,
        voice_id: config.voice_id,
        language: "en",
        agent_name: "Hotel Assistant",
      };
      await createAgent(payload);
      toast.success("Voice agent configuration saved & deployed successfully!");
      setIsDirty(false);
      setIsNeedPublish(true);
      setPublishTrigger((prev) => prev + 1);
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.message || "Failed to save agent configuration. Please try again.";
      toast.error(errMsg);
      console.error("Create agent error:", err);
    } finally {
      setSaving(false);
    }
  };

  const isEditable = keyStatus === "valid" || keyStatus === "existing";
  const selectedVoice = voices.find((v) => v.voice_id === config.voice_id);

  const filteredVoices = voices.filter((voice) => {
    const searchLower = voiceSearch.toLowerCase();
    const matchesSearch =
      !voiceSearch ||
      voice.name?.toLowerCase().includes(searchLower) ||
      getVoiceLabel(voice, "accent")?.toLowerCase().includes(searchLower) ||
      getVoiceLabel(voice, "descriptive")?.toLowerCase().includes(searchLower);
    const matchesGender =
      genderFilter === "all" ||
      getVoiceLabel(voice, "gender")?.toLowerCase() === genderFilter;
    return matchesSearch && matchesGender;
  });

  if (loading) {
    return (
      <div className="position-relative" style={{ minHeight: 300 }}>
        <div className="loaderOverlay d-flex justify-content-center align-items-center">
          <div className="loading"><div className="main-loader"></div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-setup">
      <div className="d-flex justify-content-between align-items-center section-title mb-3">
        <h5>Voice Agent Setup</h5>
        {existingAgent && (
          <span className="agent-connected-badge">
            <i className="bi bi-check-circle-fill me-1"></i>
            Agent connected&nbsp;&mdash;&nbsp;<strong>{existingAgent.agent_name || "Hotel Assistant"}</strong>
          </span>
        )}
      </div>

      {/* Provider + API Key — hidden when existing agent is loaded */}
      {!existingAgent && (
        <div className="row row-gap-4 mb-4">
          <div className="col-md-4">
            <label className="form-label">
              Voice Provider
              <InfoTooltip id="tip-provider" text="Select ElevenLabs to enable AI voice for your chatbot. You'll need an API key from your ElevenLabs dashboard." />
            </label>
            <div className="agent-provider-row">
              <button
                type="button"
                className={`agent-provider-btn ${selectedProvider === "elevenlabs" ? "active" : ""}`}
                onClick={handleSelectProvider}
              >
                <i className="bi bi-soundwave me-1"></i>
                ElevenLabs
              </button>
            </div>
          </div>

          {selectedProvider && (
            <div className="col-md-8">
              <label className="form-label">
                ElevenLabs API Key<span style={{ color: "red" }}> *</span>
                <InfoTooltip id="tip-apikey" text="Paste your ElevenLabs API key here. Find it under Profile &gt; API Keys in your ElevenLabs dashboard." />
              </label>
              <div className="agent-apikey-row">
                <div className="agent-apikey-input-wrap">
                  <input
                    type={showApiKey ? "text" : "password"}
                    className="form-control agent-apikey-input"
                    placeholder="sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={config.api_key}
                    onChange={(e) => handleChange("api_key", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleValidateKey();
                      }
                    }}
                  />
                  <button
                    className="agent-eye-btn"
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    tabIndex={-1}
                  >
                    <i className={`bi bi-eye${showApiKey ? "-slash" : ""}`}></i>
                  </button>
                </div>
                <button
                  className="agent-validate-btn"
                  type="button"
                  onClick={handleValidateKey}
                  disabled={keyStatus === "validating" || !config.api_key.trim()}
                >
                  {keyStatus === "validating" ? (
                    <><span className="spinner-border spinner-border-sm me-1"></span>Validating</>
                  ) : (
                    <><i className="bi bi-lightning-charge me-1"></i>Validate</>
                  )}
                </button>
              </div>
              {keyStatus === "valid" && (
                <small className="text-success d-block mt-1">
                  <i className="bi bi-check-circle-fill me-1"></i>Connected
                </small>
              )}
              {keyStatus === "invalid" && (
                <small className="text-danger d-block mt-1">
                  <i className="bi bi-x-circle-fill me-1"></i>Invalid key. Check your dashboard.
                </small>
              )}
            </div>
          )}
        </div>
      )}

      {/* Editable fields — shown for both existing agent and newly validated key */}
      {isEditable && (
        <>

          {/* Agent Persona & First Message */}
          <div className="row row-gap-3 mb-3">
            <div className="col-md-12">
              <label className="form-label">
                System Prompt<span style={{ color: "red" }}> *</span>
                <InfoTooltip id="tip-system-prompt" text="Define your voice agent's personality, role, and behaviour. The agent will follow these instructions when speaking with callers." />
              </label>
              <textarea
                className="form-control agent-textarea-mono"
                rows={4}
                value={config.system_prompt}
                onChange={(e) => handleChange("system_prompt", e.target.value)}
                placeholder="Define your voice agent's personality, role, and behaviour here..."
              />
              <small className="text-muted">{config.system_prompt.length} chars</small>
            </div>
            <div className="col-md-12">
              <label className="form-label">
                First Message<span style={{ color: "red" }}> *</span>
                <InfoTooltip id="tip-first-msg" text="The greeting your voice agent speaks when a caller connects. Keep it short and welcoming." />
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Hello! Welcome to our hotel..."
                value={config.first_message}
                onChange={(e) => handleChange("first_message", e.target.value)}
              />
              <small className="text-muted">Spoken when the caller connects.</small>
            </div>
          </div>

          {/* Voice grid — shown when voices are available (from get-agent or after key validation) */}
          {voices.length > 0 && (
            <>
              <hr className="mb-2" />

              <div className="agent-voice-header mb-2">
                <label className="form-label mb-0">
                  Voice Selection
                  <InfoTooltip id="tip-voice" text="Choose a voice for your agent. Use the search and gender filters to find the right tone for your property." />
                  <span className="text-muted ms-2" style={{ fontWeight: 400 }}>
                    ({filteredVoices.length} of {voices.length})
                  </span>
                </label>
                <div className="agent-voice-filters">
                <div className="agent-voice-search-wrap">
                  <i className="bi bi-search agent-voice-search-icon"></i>
                  <input
                    type="text"
                    className="form-control agent-voice-search"
                    placeholder="Search voices..."
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                  />
                </div>
                <div className="agent-voice-gender-btns">
                  {["all", "male", "female"].map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`agent-voice-gender-btn ${genderFilter === g ? "active" : ""}`}
                      onClick={() => setGenderFilter(g)}
                    >
                      {g === "all" ? "All" : formatLabel(g)}
                    </button>
                  ))}
                </div>
                </div>
              </div>

              {filteredVoices.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13 }}>No voices match your filters.</p>
              ) : (
                <div className="agent-vc-grid">
                  {filteredVoices.map((voice) => {
                    const displayName = getVoiceDisplayName(voice);
                    const description = getVoiceDescription(voice);
                    const accent = formatLabel(getVoiceLabel(voice, "accent"));
                    const gender = formatLabel(getVoiceLabel(voice, "gender"));
                    const isSelected = config.voice_id === voice.voice_id;
                    const isPlaying = playingVoiceId === voice.voice_id;
                    return (
                      <div
                        key={voice.voice_id}
                        className={`agent-vc ${isSelected ? "selected" : ""}`}
                        onClick={() => handleChange("voice_id", voice.voice_id)}
                      >
                        <div className={`agent-vc-avatar ${isSelected ? "active" : ""}`}>
                          {displayName.charAt(0)}
                        </div>
                        <div className="agent-vc-details">
                          <span className="agent-vc-name">{displayName}</span>
                          {description && <span className="agent-vc-style">{description}</span>}
                          <span className="agent-vc-meta">
                            {[accent, gender].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                        <button
                          className={`agent-vc-play ${isPlaying ? "playing" : ""}`}
                          type="button"
                          title={voice.preview_url ? (isPlaying ? "Stop" : "Play") : "No preview"}
                          disabled={!voice.preview_url}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewVoice(voice);
                          }}
                        >
                          <i className={`bi bi-${isPlaying ? "stop-fill" : "play-fill"}`}></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedVoice && (
                <div className="agent-voice-selected-bar mt-2">
                  <i className="bi bi-check-circle me-1"></i>
                  <strong>{getVoiceDisplayName(selectedVoice)}</strong>
                  <span className="ms-1 text-muted">
                    {[formatLabel(getVoiceLabel(selectedVoice, "accent")), formatLabel(getVoiceLabel(selectedVoice, "gender"))].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </>
          )}

          {keyStatus === "valid" && voices.length === 0 && (
            <>
              <hr className="mb-3" />
              <label className="form-label mb-2">Voice Selection</label>
              <p className="text-muted" style={{ fontSize: 13 }}>
                <i className="bi bi-exclamation-triangle me-1"></i>
                No voices found. Check your subscription.
              </p>
            </>
          )}
        </>
      )}

      {isEditable && isDirty && (
        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
            ) : (
              "Save"
            )}
          </button>
        </div>
      )}

      <audio ref={audioElRef} preload="none" style={{ display: "none" }} />
    </div>
  );
};

export default AgentSetup;
